import React, { useEffect } from "react";
import ReactModal from "react-modal";
import { cls } from "@wss/cls";

type HeaderProps = {
  children: React.ReactNode;
  closeModal: () => void;
  className?: string;
  style?: React.CSSProperties;
};

type FooterProps = {
  children: React.ReactNode;
  className?: string;
};

type BodyProps = {
  children: React.ReactNode;
  className?: string;
};

export const Header = ({
  children,
  closeModal,
  className = "",
  style = {},
}: HeaderProps) => (
  <header
    data-testid="modal-header"
    className={cls(
      "border-gray-200",
      "border-solid",
      "border-0",
      "border-b",
      "flex",
      "items-center",
      "p-3",
      className,
    )}
    style={style}
  >
    {children}
    <button
      type="button"
      aria-label="Close"
      onClick={closeModal}
      className={cls(
        "bg-transparent",
        "border-none",
        "cursor-pointer",
        "flex",
        "font-bold",
        "text-xl",
        "mb-2",
        "ml-auto",
        "opacity-50",
        "pr-3",
      )}
      style={{ textShadow: "0 1px 0 #fff" }}
    >
      &#10799;
    </button>
  </header>
);

export const Body = ({ children, className = "" }: BodyProps) => (
  <main data-testid="modal-body" className={cls("flex-1 m-5", className)}>
    {children}
  </main>
);

export const Footer = ({ children, className = "" }: FooterProps) => (
  <footer
    data-testid="modal-footer"
    className={cls(
      "bg-gray-50",
      "border-gray-200",
      "border-solid",
      "border-0",
      "border-t",
      "flex",
      "justify-end",
      "p-3",
      className,
    )}
  >
    {children}
  </footer>
);

type ModalProps = {
  isOpen: boolean;
  onRequestClose?: (
    event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
  ) => void;
  onAfterOpen?: ReactModal.OnAfterOpenCallback;
  children: React.ReactNode;
  className: string;
  role?: string;
  contentStyle?: React.CSSProperties;
  overlayStyle?: React.CSSProperties;
  preventScroll?: boolean;
  closeTimeoutMS?: number;
  overFlowY?: string;
};

export default ({
  isOpen,
  onRequestClose,
  onAfterOpen,
  children,
  className,
  role,
  contentStyle,
  overlayStyle,
  preventScroll,
  closeTimeoutMS,
  overFlowY,
}: ModalProps) => {
  useEffect(() => {
    ReactModal.setAppElement("body");
  }, []);

  return (
    <ReactModal
      isOpen={isOpen}
      onAfterOpen={onAfterOpen}
      onRequestClose={onRequestClose}
      role={role}
      overlayClassName="fixed inset-0 overflow-y-auto"
      className={cls(
        "bg-white",
        "border-gray-200",
        "rounded",
        "border-solid",
        "border",
        "mx-auto",
        "mt-5",
        "opacity-100",
        "outline-none",
        overFlowY ?? "overflow-y-auto",
        "overflow-x-hidden",
        "absolute",
        "inset-x-0",
        "shadow-lg",
        "w-11/12",
        className,
      )}
      style={{
        // Product detail page sticky banner has a z-index of 40, nav header is 55, toasts are 82
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          zIndex: 80,
          overflowY: "auto",
          ...overlayStyle,
        },
        content: {
          ...contentStyle,
        },
      }}
      preventScroll={preventScroll}
      closeTimeoutMS={closeTimeoutMS}
      bodyOpenClassName="ReactModal__Body--open"
    >
      {children}
    </ReactModal>
  );
};
